import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { ElectionResults } from "../../../../flexistore/shared/election_results";
import { safe_get } from "../../../../report-visuals";
import { useStore } from "../../../../helpers";
import type { SourceFile } from "../sidebar/sources-types";
import { copyTableAsPng } from "../../../survey-management/copy-table-image";
import TARSChartCopyImageToolbar from "../../tars-charts/TARSChartCopyImageToolbar";
import DataBankEmptyState from "./DataBankEmptyState";

type Props = { file: SourceFile };

export default function DataBankSourceElectionPanel({ file }: Props) {
    const { flexiStore } = useStore();
    const fsdId = file.data_bank_fsd_id!;
    const electionKey = file.data_bank_election!;
    const snapshotRef = useRef<HTMLDivElement>(null);
    const copyImageRef = useRef<(() => Promise<void>) | null>(null);

    const { data, isLoading, isError } = useQuery({
        queryKey: ["tars-data-bank-election-view", fsdId, electionKey],
        retry: false,
        queryFn: async () => {
            try {
                if (fsdId <= 0 || !electionKey) return null;
                await flexiStore.get_fsdata(fsdId);
                const path = safe_get(flexiStore.current_fsd?.r1, "election_results", "");
                if (!path || typeof path !== "string") return null;
                await flexiStore.load_s3_json_resource("urn-fsd", path, "tars_databank_election_view");
                const all = flexiStore.external_resource;
                if (!all || typeof all !== "object" || Array.isArray(all)) return null;
                return (all as Record<string, unknown>)[electionKey] ?? null;
            } catch {
                return null;
            }
        },
    });

    useEffect(() => {
        if (!data) {
            copyImageRef.current = null;
            return;
        }
        copyImageRef.current = async () => {
            const el = snapshotRef.current;
            if (!el) {
                toast.error("Nothing to copy yet.");
                return;
            }
            if (!navigator.clipboard?.write) {
                toast.error("Clipboard is not available (use HTTPS or localhost).");
                return;
            }
            try {
                const dpr = typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1;
                const pixelRatio = Math.min(5, Math.max(4, dpr * 2));
                await copyTableAsPng(el, { pixelRatio, skipAutoScale: true });
                toast.success("Election results copied as image — paste into documents or chat");
            } catch {
                toast.error("Could not copy image to clipboard");
            }
        };
    }, [data]);

    if (isLoading) {
        return (
            <div className="flex min-h-[200px] flex-1 items-center justify-center gap-2 text-sm text-gray-500">
                <Oval
                    height={32}
                    width={32}
                    color="#64748b"
                    secondaryColor="#e2e8f0"
                    ariaLabel="Loading"
                    visible
                />
                Loading election results…
            </div>
        );
    }
    if (isError) {
        return (
            <DataBankEmptyState
                variant="error"
                message="Something went wrong while loading election results. Try again later."
            />
        );
    }
    if (!data) {
        return (
            <DataBankEmptyState
                message={`No election results are available for "${electionKey}" in this assembly constituency.`}
            />
        );
    }

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 bg-white/90 px-4 py-3">
                <p className="text-[12px] font-medium text-slate-500">Past election results</p>
                <TARSChartCopyImageToolbar
                    copyRef={copyImageRef}
                    buttonLabel="Copy as image"
                    ariaLabel="Copy election results as image"
                />
            </div>
            <div className="p-4">
                <div
                    ref={snapshotRef}
                    className="rounded-lg border border-gray-100 bg-white p-4"
                    style={{ overflow: "visible" }}
                >
                    <div className="mb-3 border-b border-gray-100 pb-2 text-lg font-bold uppercase text-gray-900">
                        {electionKey}
                    </div>
                    <ElectionResults name={electionKey} ds={data} />
                </div>
            </div>
        </div>
    );
}
